import {
  getDefaultShipper,
  getDefaultPickupDateTimeInfo,
  getDefaultPickupDateTimeErrorInfo,
  getDefaultShipperError,
} from "./ShipperInfo/utils";
import {
  getDefaultShipment,
  getDefaultShipmentError,
} from "./ShipmentInfo/utils";
import {
  getDefaultEditReasonInfo,
  getDefaultEditReasonErrorInfo,
} from "./EditReasonInfo/utils";
import { getDefaultBillingInfo } from "./Billing/utils";
import { PICKUP_MANAGEMENT_MODES, WARNING_DETAILS } from "./constants";
import { convertDateTimeToTimeString } from '@dataGrid/gridUtilities';
import { format } from "date-fns";
import { getDataWithQueryString, getDataWithDeduplication } from "../../../../../../api/componentAPIs";
import Label from "./MergePickupShipperDetails/RadioLabel"
import { getRadioLabelData } from "./MergePickupShipperDetails/utils";
import { SVG_ICON_NAME } from "../../../../../../appConstants";
const getDefaultPickupManagementInfo = (pickup, terminalOffset) => {
  return {
    shipperInfo: getDefaultShipper(pickup),
    shipmentInfo: pickup
      ? pickup.shipments.map((shipment) => getDefaultShipment(shipment))
      : [],
    pickupDateTimeInfo: getDefaultPickupDateTimeInfo(pickup, terminalOffset),
    specialInstructions: pickup?.specialInstructions ?? "",
    billingInfo: getDefaultBillingInfo(pickup),
    editReasonInfo: getDefaultEditReasonInfo(pickup),
    isSwapPickup: pickup?.isSwapPickup ?? false,
    isDockDropPickup: pickup?.isDockDropPickup ?? false,
    isExhibitPickup: pickup?.isExhibitPickup ?? false,
    pickupId: pickup?.pickupId || '',
    isLiftGate : pickup?.isLiftGate ?? false,
  };
};

const getDefaultPickupManagementErrorInfo = () => {
  return {
    pickupDateTimeInfo: getDefaultPickupDateTimeErrorInfo(),
    shipperInfo: getDefaultShipperError(),
    shipmentInfo: [getDefaultShipmentError()],
    editReasonInfo: getDefaultEditReasonErrorInfo(),
    swapAndDrop: "",
  };
};

const getSavePickupMessage = (mode) => {
  switch (mode) {
    case PICKUP_MANAGEMENT_MODES.CREATE_PICKUP:
      return {
        successMsg: "Pickup Created Successfully",
        errorMsg: "Create Pickup Failed",
      };
    case PICKUP_MANAGEMENT_MODES.EDIT_PICKUP:
      return {
        successMsg: "Pickup Updated Successfully",
        errorMsg: "Edit Pickup Failed",
      };
    case PICKUP_MANAGEMENT_MODES.MERGE_PICKUP:
      return {
        successMsg: "Pickups Merged Successfully",
        errorMsg: "Merge Pickups Failed",
      };
    case PICKUP_MANAGEMENT_MODES.NO_FREIGHT_PICKUP:
      return {
        successMsg: "No Freight Pickup Successful",
        errorMsg: "No Freight Pickup Failed",
      };
    default:
      return {
        successMsg: "Void Pickup Successful",
        errorMsg: "Void Pickup Failed",
      };
  }
};

const isReadOnlyPickupViewMode = (mode) => {
  return [
    PICKUP_MANAGEMENT_MODES.VOID_PICKUP,
    PICKUP_MANAGEMENT_MODES.UNVOID_PICKUP,
    PICKUP_MANAGEMENT_MODES.NO_FREIGHT_PICKUP
  ].includes(mode);
};

const isMergePickupMode = (mode) => {
  return PICKUP_MANAGEMENT_MODES.MERGE_PICKUP === mode;
}

const isCreatePickupMode = (mode) => {
  return PICKUP_MANAGEMENT_MODES.CREATE_PICKUP === mode;
}

const isCreateOrEditPickupMode = (mode) => {
  return [
    PICKUP_MANAGEMENT_MODES.CREATE_PICKUP,
    PICKUP_MANAGEMENT_MODES.EDIT_PICKUP
  ].includes(mode);
}

const isEditOrMergePickupMode = (mode) => {
  return [PICKUP_MANAGEMENT_MODES.EDIT_PICKUP, PICKUP_MANAGEMENT_MODES.MERGE_PICKUP].includes(mode);
};

const getConfirmationButtonLabel = (mode,driver) => {
  switch (mode) {
    case PICKUP_MANAGEMENT_MODES.CREATE_PICKUP:
      return "Create Pickup";
    case PICKUP_MANAGEMENT_MODES.EDIT_PICKUP:
      return "Done";
    case PICKUP_MANAGEMENT_MODES.MERGE_PICKUP:
      return driver ? "Merge Pickups & Assign Driver":"Merge Pickups";
    case PICKUP_MANAGEMENT_MODES.NO_FREIGHT_PICKUP:
      return "Mark as No Freight"
    case PICKUP_MANAGEMENT_MODES.VOID_PICKUP:
      return "Void Pickup";
    default:
      return "Unvoid Pickup";
  }
};

const checkIsDateTimeUpdated = (pickupData, pickupDateTimeInfo) => {
  const { pickupReadyTime, pickupCloseTime, pickupDate } = pickupData;
  const { readyTime, closeTime } = pickupDateTimeInfo;
  return (
    convertDateTimeToTimeString(readyTime) !== pickupReadyTime ||
    convertDateTimeToTimeString(closeTime) !== pickupCloseTime ||
    pickupDate !== format(pickupDateTimeInfo.pickupDate, "yyyy-MM-dd")
  );
};

const getSelectedTerminalDetails = (selectedTerminal, terminals) => {
  return terminals.find((terminal) => terminal.value === selectedTerminal);
};

const getWarningLabel = (mode) => {
  const {WARNING_LABELS: {CREATE, EDIT, MERGE}} = WARNING_DETAILS;
  const warningLabel = {
    [PICKUP_MANAGEMENT_MODES.CREATE_PICKUP]: CREATE,
    [PICKUP_MANAGEMENT_MODES.EDIT_PICKUP]: EDIT,
    [PICKUP_MANAGEMENT_MODES.MERGE_PICKUP]: MERGE,
  }
  return warningLabel[mode]
}

const getFilteredShipmentsData = (shipmentInfo) => {
  return shipmentInfo.reduce((acc, shipment, index) => {
    const {pickupShipmentRowId, ...shipmentData} = shipment;
    if (Object.values(shipmentData).filter(Boolean).length === 0) {
      return acc;
    }
    return [
      ...acc,
      {
        ...shipmentData,
        handlingUnits : shipmentData.handlingUnits || 0,
        pieces : shipmentData.pieces || 0,
        weight : shipmentData.weight || 0,
      },
    ];
  }, []);
};

const getFormattedShippers = (pickupData, selectedPossibleMergePickupId) => {
  const selectedPickupId = selectedPossibleMergePickupId?.toLowerCase();
  let newPickupData = pickupData;
  if (selectedPickupId) {
    /* if possible merge pickup icon click on grid select that shipper in 1st position */
    newPickupData = moveSelectedPossibleMergePickupToFirstPosition(
      pickupData,
      selectedPickupId
    );
  }

  return newPickupData.map((pickup) => {
    return {
      label: <Label data={getRadioLabelData(pickup)} pickupId={pickup.pickupId} />,
      value: JSON.stringify({
        rmaxShipperCode: pickup.rmaxShipperCode,
        pickupId: pickup.pickupId
      }),
      pickupId: pickup.pickupId
    };
  });
};

const getFormattedTerminals = (terminals) => {
  return terminals.map((terminal) => ({
    label: terminal.terminalCode,
    value: terminal.terminalCode,
    offset: terminal.utcToLocalOffset,
  }));
}

const fetchTerminalAndRestrictionsData = (terminalCode) => {
  return Promise.all([
    getDataWithQueryString(
      "GetAllAppTerminals",
      `terminalCode=${terminalCode}`
    ),
    getDataWithQueryString(
      "PickupRestrictions",
      `terminalCode=${terminalCode}`
    ),
  ]);
};

const getCombinedSpecialInstructions = (specialInstructions) => {
  return specialInstructions.join(" ").trim();
}

const fetchLocationMappingData = ({
  terminalCode,
  zipCode,
}) => {
  return getDataWithDeduplication(
    "GetLocationMapping",
    `terminalCode=${terminalCode}&zip=${zipCode}`,
  );
};

const getFirstSelectedAssignedDriverPickup = (pickupData) => {
  return pickupData.find(pickup => pickup.driverId);
}

const moveSelectedPossibleMergePickupToFirstPosition = (
  pickupData,
  selectedPickupId
) => {
  const selectedPickupIndex = pickupData.findIndex(
    (pickup) => pickup.pickupId.toLowerCase() === selectedPickupId
  );
  const newPickupData = [...pickupData];
  if (selectedPickupIndex !== -1) {
    const selectedPickup = newPickupData.splice(selectedPickupIndex, 1);
    return [...selectedPickup, ...newPickupData];
  }
  return pickupData;
};

const getShipperByPickupId = (shipperList, pickupId) => {
  if (!pickupId) {
    return null;
  }
  return (
    shipperList.find(
      (shipper) => shipper.pickupId.toLowerCase() === pickupId.toLowerCase()
    )?.value ?? null
  );
};

const validateToggle = ({ isDockDropPickup, isSwapPickup }) => {
  return isSwapPickup && isDockDropPickup
    ? "Pickup can't be swap and dock drop enabled at the same time"
    : null;
};

const pickupTypeValueGetter = (params) => {
  const { data } = params;
  let pickupTypes = data.priority ? [...data.priority] : [];
  if (data.is3PlPickup) {
    pickupTypes.push(SVG_ICON_NAME._3PL);
  }
  if (data.isSwapPickup) {
    pickupTypes.push(SVG_ICON_NAME.SWAP);
  }
  if (data.isDockDropPickup) {
    pickupTypes.push(SVG_ICON_NAME.DockDrop);
  }
  if (data.isExhibitPickup) {
    pickupTypes.push(SVG_ICON_NAME.ExhibitPickup);
  }
  if (data.isDailyPickup) {
    pickupTypes.push(SVG_ICON_NAME.dailyPickup);
  }
  return pickupTypes.filter(Boolean);
};

const getReasonCodeOptions = (reasonCodeList) => {
  return reasonCodeList.map((reasonCode) => ({
    labelText: reasonCode.label,
    value: String(reasonCode.value),
  }));
}

export {
  getDefaultPickupManagementInfo,
  getDefaultPickupManagementErrorInfo,
  getSavePickupMessage,
  checkIsDateTimeUpdated,
  isReadOnlyPickupViewMode,
  isMergePickupMode,
  getConfirmationButtonLabel,
  getSelectedTerminalDetails,
  getWarningLabel,
  getFilteredShipmentsData,
  getFormattedShippers,
  getFormattedTerminals,
  fetchTerminalAndRestrictionsData,
  isCreatePickupMode,
  isCreateOrEditPickupMode,
  getCombinedSpecialInstructions,
  fetchLocationMappingData,
  getFirstSelectedAssignedDriverPickup,
  getShipperByPickupId,
  validateToggle,
  isEditOrMergePickupMode,
  pickupTypeValueGetter,
  getReasonCodeOptions
};
